/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { useState } from "react";
import { TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import useHttp from "hooks/http.hook";
import { UserInterface } from "medicalsystem-shared";
import { CoreAPI_URLs } from "constants/http.const";
import PasswordRecoveryCard from "components/generals/PasswordRecoveryCard";
import Input from "components/generals/Input";

/**
 * Properties for the SearchAccounting component.
 * 
 * @interface SearchAccountingProps
 * @property {Function} onNext - Callback with found user data
 */
interface SearchAccountingProps {
  onNext: (user: UserInterface) => void;
}

/**
 * Search account component for password recovery initiation.
 * First step in password recovery flow to identify user account.
 * 
 * Features:
 * - Email input field
 * - Account search by email
 * - User validation
 * - Error messaging
 * - Loading state
 * - API integration
 * - i18n translation support
 * - Progress to verification step
 * 
 * Search Flow:
 * 1. User enters email address
 * 2. Validates email not empty
 * 3. Searches for account in database
 * 4. Sends verification code to email
 * 5. Advances to verification step with user data
 * 
 * Error Handling:
 * - Required field validation
 * - User not found message
 * - API error handling
 * - User-friendly error display
 * 
 * @param {SearchAccountingProps} props - Component properties
 * @returns {JSX.Element} Account search form
 */
const SearchAccounting = ({ onNext }: SearchAccountingProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, , sendRequest] = useHttp();

  const handleSearch = async () => {
    setError("");
    if (!email) {
      setError(t("passwordRecovery.requiredFields"));
      return;
    }

    sendRequest(
      CoreAPI_URLs.PASSWORD_RECOVERY_SEARCH,
      onNext,
      "POST",
      JSON.stringify({ email }),
      null,
      { applyErrorFn: (_message) => setError(t("passwordRecovery.userNotFound")) }
    );
  };

  return (
    <PasswordRecoveryCard
      title={t("passwordRecovery.title")}
      description={t("passwordRecovery.description")}
      error={error}
      footer={
        <Button variant="contained" color="info" onClick={handleSearch} disabled={isLoading}>
          {t("passwordRecovery.search")}
        </Button>
      }
    >
      <Input
        label={t("passwordRecovery.email")}
        variant="outlined"
        fullWidth
        sx={{ mt: 2 }}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
    </PasswordRecoveryCard>
  );
};

export default SearchAccounting;
